/*
  Note: Written to assume there may be multiple headers on a single page (which may be the
        case on some mason pages using jQuery mobile)
*/
export default function headerUI() {
    return function () {
        function debounce(func: typeof onResize, wait: number) {
            /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
            let timeout: any;
            return function () {
                const later = function () {
                    timeout = null;
                    func();
                };
                clearTimeout(timeout);
                timeout = setTimeout(later, wait);
            };
        }

        function isMobile() {
            return window.innerWidth < 960;
        }

        function isMenuHeading(target: HTMLElement) {
            return target.hasAttribute('data-universal-header-menu');
        }

        function isHamburgerButton(target: HTMLElement) {
            return target.hasAttribute(
                'data-universal-header-hamburger-button'
            );
        }

        function getAllHeaders() {
            return [].slice.call(
                document.querySelectorAll('[data-universal-header]')
            );
        }

        function getAllHeadersWithMobileMenuOpen() {
            return getAllHeaders().filter(function (header: HTMLElement) {
                return header.hasAttribute(
                    'data-universal-header-mobile-menu-open'
                );
            });
        }

        function isModalOpen() {
            return document.querySelectorAll('[data-modal-open]').length > 0;
        }

        function isMobileMenuOpen() {
            return getAllHeadersWithMobileMenuOpen().length > 0;
        }

        function closeMobileMenu() {
            getAllHeadersWithMobileMenuOpen().forEach(function (
                header: HTMLElement
            ) {
                header.removeAttribute(
                    'data-universal-header-mobile-menu-open'
                );
            });
        }

        function resetMobileMenuAccordions() {
            const allMenuItems = [].slice.call(
                document.querySelectorAll(
                    '[data-universal-header-accordion-open]'
                )
            );
            allMenuItems.forEach(function (item: HTMLElement) {
                item.removeAttribute('data-universal-header-accordion-open');

                if (
                    item.nextSibling &&
                    (item.nextSibling as HTMLElement).style.maxHeight
                ) {
                    (item.nextSibling as HTMLElement).style.maxHeight =
                        null as unknown as string;
                }
            });
        }

        function setOverFlow() {
            if (isMobile() && isMobileMenuOpen()) {
                window.scrollTo(0, 0);
                document.body.style.overflow = 'hidden';
            } else if (isModalOpen()) {
                // if modal opened, only keeps overflow to hidden without scrolling to the top
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = 'visible';
            }
        }

        function updateHamburgerMenuTrackingLabel() {
            const isMenuOpen = isMobileMenuOpen();
            const closedTrackingValue = 'open burger';
            const openedTrackingValue = 'close burger';

            getAllHeaders().forEach(function (header: HTMLElement) {
                const hamburgerElement: HTMLElement | null =
                    header.querySelector(
                        '[data-universal-header-hamburger-button]'
                    );

                // We're looking for any data-* attribute with the value of "(open | close) burger"
                // and updating them based on the state of the mobile menu (open or closed). The reason
                // for this slightly convoluted approach is that CBv2 copies the tracking attributes
                // to a different naming convention. We really shouldn't know about the specific naming
                // convention in Blueprint so the cleanest method is to just look for the items based
                // on value and update any found
                if (!hamburgerElement) {
                    return;
                }
                for (const key in hamburgerElement.dataset) {
                    const dataAttributeValue = hamburgerElement.dataset[key];

                    if (
                        isMenuOpen &&
                        dataAttributeValue === closedTrackingValue
                    ) {
                        setTimeout(function () {
                            hamburgerElement.dataset[key] = openedTrackingValue;
                        }, 500);
                    } else if (
                        !isMenuOpen &&
                        dataAttributeValue === openedTrackingValue
                    ) {
                        setTimeout(function () {
                            hamburgerElement.dataset[key] = closedTrackingValue;
                        }, 500);
                    }
                }
            });
        }

        function onResize() {
            if (!isMobile()) {
                closeMobileMenu();
                resetMobileMenuAccordions();
            }
            setOverFlow();

            getAllHeaders().forEach(function (header: HTMLElement) {
                header.classList.remove('is-resizing');
            });
        }

        function init() {
            const onResizeDebounce = debounce(onResize, 100);

            window.addEventListener('resize', function () {
                getAllHeaders().forEach(function (header: HTMLElement) {
                    header.classList.add('is-resizing');
                });

                onResizeDebounce();
            });

            document.addEventListener('click', function (e: MouseEvent) {
                if (!isMobile() || !e.target) {
                    return;
                }

                if (isMenuHeading(e.target as HTMLElement)) {
                    const nextSibling = (e.target as HTMLElement).nextSibling;

                    if (
                        (e.target as HTMLElement).hasAttribute(
                            'data-universal-header-accordion-open'
                        )
                    ) {
                        // Close accordion menu
                        (e.target as HTMLElement).removeAttribute(
                            'data-universal-header-accordion-open'
                        );
                        if (nextSibling) {
                            (nextSibling as HTMLElement).style.maxHeight =
                                null as unknown as string;
                        }
                    } else {
                        // Open accordion menu
                        (e.target as HTMLElement).setAttribute(
                            'data-universal-header-accordion-open',
                            ''
                        );

                        if (nextSibling) {
                            (nextSibling as HTMLElement).style.maxHeight =
                                String(
                                    (nextSibling as HTMLElement).scrollHeight
                                ) + 'px';
                        }
                    }
                }

                if (isHamburgerButton(e.target as HTMLElement)) {
                    const isMenuOpen = isMobileMenuOpen();

                    getAllHeaders().forEach(function (header: HTMLElement) {
                        const buttonElement =
                            header.querySelector('[aria-expanded]');

                        if (isMenuOpen) {
                            header.removeAttribute(
                                'data-universal-header-mobile-menu-open'
                            );
                        } else {
                            header.setAttribute(
                                'data-universal-header-mobile-menu-open',
                                ''
                            );
                        }

                        if (buttonElement) {
                            buttonElement.setAttribute(
                                'aria-expanded',
                                !isMenuOpen as unknown as string
                            );
                        }
                    });

                    updateHamburgerMenuTrackingLabel();
                    setOverFlow();
                }
            });
        }

        window.addEventListener('DOMContentLoaded', init);
    };
}
